import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { getPlayers, getGPS, getScenarios, getLoadScenarios } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { TARGETS, STATUS_COLORS, getStatus, getPct } from '../GPSData';

const GAUGE_METRICS = [
  { key: 'TD',   label: 'Total Dist.' },
  { key: 'HID',  label: 'HID >19.8'  },
  { key: 'SD',   label: 'Sprint >25.2' },
  { key: 'SA',   label: 'Short Acts.' },
  { key: 'HMLD', label: 'HMLD'        },
];

const fmtK = v => {
  if (v == null || v === 0) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return Math.round(v).toString();
};

// ── Compact arc gauge (140px wide, fixed) ──────────────────────────────────
function MobileGauge({ label, value, targets }) {
  const status = getStatus(value, targets);
  const color  = STATUS_COLORS[status];
  const pct    = getPct(value, targets);

  const R = 38; const cx = 55; const cy = 54;
  const startDeg = -200; const sweepDeg = 220;
  const toRad = d => (d * Math.PI) / 180;

  const arcPath = pctFill => {
    const clamp = Math.min(Math.max(pctFill, 0), 1);
    const endDeg = startDeg + sweepDeg * clamp;
    const x1 = cx + R * Math.cos(toRad(startDeg));
    const y1 = cy + R * Math.sin(toRad(startDeg));
    const x2 = cx + R * Math.cos(toRad(endDeg));
    const y2 = cy + R * Math.sin(toRad(endDeg));
    const large = sweepDeg * clamp > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`;
  };

  const fill = targets?.upper > 0 ? Math.min(value / targets.upper, 1.15) : 0;
  const lowerFrac = targets?.upper > 0 ? targets.lower / targets.upper : null;

  return (
    <div style={{ width: 140, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
      <svg width="110" height="82" viewBox="0 0 110 82" style={{ overflow: 'visible' }}>
        <path d={arcPath(1)} fill="none" stroke="var(--gauge-track, #1e293b)" strokeWidth="8" strokeLinecap="round" />
        {lowerFrac && (
          <path d={arcPath(lowerFrac)} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeOpacity="0.2" />
        )}
        <path d={arcPath(Math.min(fill, 1))} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />
        <text x="55" y="52" textAnchor="middle" fill={color} fontSize="13" fontWeight="700" fontFamily="Inter,sans-serif">
          {fmtK(value)}
        </text>
        {pct !== null && (
          <text x="55" y="65" textAnchor="middle" fill="var(--text-muted)" fontSize="9" fontFamily="Inter,sans-serif">{pct}%</text>
        )}
      </svg>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center', marginTop: -2 }}>
        {label}
      </div>
      {targets?.upper > 0 && (
        <div style={{ fontSize: 9, color: 'var(--text-dimmed)', textAlign: 'center', marginTop: 2 }}>
          {Math.round(targets.lower).toLocaleString()}–{Math.round(targets.upper).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ── Player table (6 cols: TD HID SD SA HMLD Spd, tap a column header to sort) ──
const TABLE_COLS = [
  { key: 'td',        label: 'TD',   fmt: fmtK,                                    tgtKey: 'TD',   desc: 'Total Distance' },
  { key: 'hid',       label: 'HID',  fmt: fmtK,                                    tgtKey: 'HID',  desc: 'High Intensity Distance (>19.8 km/u)' },
  { key: 'sd',        label: 'SD',   fmt: fmtK,                                    tgtKey: 'SD',   desc: 'Sprint Distance (>25.2 km/u)' },
  { key: 'sa',        label: 'SA',   fmt: v => v ? Math.round(v).toString() : '—', tgtKey: 'SA',   desc: 'Short Actions' },
  { key: 'hmld',      label: 'HMLD', fmt: fmtK,                                    tgtKey: 'HMLD', desc: 'High Metabolic Load Distance' },
  { key: 'max_speed', label: 'SPD',  fmt: v => v ? `${v}` : '—',                  tgtKey: null,   desc: 'Max Speed' },
];

function statusChar(val, tgt) {
  if (!tgt || (tgt.upper === 0 && tgt.lower === 0)) return null;
  const s = getStatus(val, tgt);
  if (s === 'green') return { char: '✓', color: '#10b981' };
  if (s === 'above') return { char: '↑', color: '#7c3aed' };
  if (s === 'amber') return { char: '~', color: '#f59e0b' };
  if (s === 'below') return { char: '↓', color: '#ef4444' };
  return null;
}

function PlayerTable({ players, myName }) {
  const [sortKey, setSortKey] = useState('hmld');
  const [sortDir, setSortDir] = useState('desc');

  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      const av = a.metrics[sortKey] ?? 0;
      const bv = b.metrics[sortKey] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [players, sortKey, sortDir]);

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const TH = {
    padding: '7px 5px', fontSize: 9, fontWeight: 700,
    color: 'var(--text-muted)', textAlign: 'right',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    background: 'var(--bg-app)', borderBottom: '1px solid var(--border-strong)',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
  };
  const TD = { padding: '7px 5px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

  return (
    <>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 10, border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: 'left', paddingLeft: 12, minWidth: 60, cursor: 'default' }}>Name</th>
              {TABLE_COLS.map(c => {
                const active = sortKey === c.key;
                const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th key={c.key} style={{ ...TH, color: active ? 'var(--text-primary)' : 'var(--text-muted)' }} onClick={() => toggleSort(c.key)}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>{c.label} <Icon size={9} /></span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((p, i) => {
            const isMe = !!(myName && p.name?.toLowerCase() === myName);
            const firstName = p.name?.split(' ')[0] || '—';
            const hmlStatus = getStatus(p.metrics.hmld ?? 0, p.targets?.HMLD);
            return (
              <tr key={p.player_id || p.name} style={{
                borderTop: '1px solid var(--border)',
                background: isMe
                  ? 'rgba(59,130,246,0.1)'
                  : i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent',
              }}>
                <td style={{ ...TD, textAlign: 'left', paddingLeft: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[hmlStatus], flexShrink: 0 }} />
                    <span style={{
                      fontSize: 13, fontWeight: isMe ? 700 : 500,
                      color: isMe ? '#60a5fa' : 'var(--text-primary)',
                      filter: !isMe ? 'blur(5px)' : 'none',
                      userSelect: !isMe ? 'none' : 'auto',
                    }}>
                      {firstName}
                    </span>
                  </div>
                </td>
                {TABLE_COLS.map(c => {
                  const val = p.metrics[c.key] ?? 0;
                  const tgt = c.tgtKey ? p.targets?.[c.tgtKey] : null;
                  const color = tgt ? STATUS_COLORS[getStatus(val, tgt)] : 'var(--text-secondary)';
                  const si = c.tgtKey ? statusChar(val, tgt) : null;
                  return (
                    <td key={c.key} style={{ ...TD, color }}>
                      {c.fmt(val)}
                      {si && <span style={{ fontSize: 9, color: si.color, marginLeft: 2 }}>{si.char}</span>}
                    </td>
                  );
                })}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend — same order as the table columns above */}
      <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {TABLE_COLS.map(c => (
            <div key={c.key} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>{c.label}</strong> = {c.desc}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function PlayerTeamTraining({ playerRecord, mode = 'team', initialSessionId = null }) {
  const { selectedSeasons } = useSeason();
  const [sessions,  setSessions]  = useState([]);
  const [players,   setPlayers]   = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selected,  setSelected]  = useState('');
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s, sc, lsc] = await Promise.all([getPlayers(), getGPS(), getScenarios(), getLoadScenarios()]);
        setPlayers(p);
        setScenarios((sc || []).map(a => ({
          ...a, startDate: a.start_date?.slice(0, 10), playerId: a.player_id,
        })));
        setCustomTemplates(lsc || []);
        const normalized = s.map(sess => ({
          ...sess,
          date:    sess.session_date?.slice(0, 10),
          title:   sess.session_title,
          players: sess.players.map(pl => ({ ...pl, mdLabel: pl.md_label, playerId: pl.player_id })),
        }));
        setSessions(normalized);
        if (normalized.length) {
          // Deep-linked from elsewhere (e.g. tapping a session in the player Today timeline) —
          // land on that exact session instead of always defaulting to the latest one.
          const requested = initialSessionId && normalized.find(s => String(s.id) === String(initialSessionId));
          const target = requested || [...normalized].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
          setSelected(String(target.id));
        }
      } catch (err) {
        console.error('PlayerTeamTraining load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredSessions = useMemo(
    () => sessions.filter(s => {
      if (!s.date) return true;
      const season = dateToSeason(s.date);
      if (season && selectedSeasons.length > 0 && !selectedSeasons.includes(season)) return false;
      // A rehab-only day has no team players, and a normal team day has no
      // rehab players — either way, a session with nothing relevant to the
      // current mode shouldn't clutter the session picker.
      const relevant = mode === 'rehab' ? p => p.is_rehab : p => !p.is_rehab;
      return (s.players || []).some(relevant);
    }),
    [sessions, selectedSeasons, mode],
  );

  useEffect(() => {
    if (!filteredSessions.length) { setSelected(''); return; }
    if (!filteredSessions.find(s => String(s.id) === selected)) {
      const latest = [...filteredSessions].sort((a, b) => b.date.localeCompare(a.date))[0];
      setSelected(String(latest.id));
    }
  }, [filteredSessions]);

  const session = filteredSessions.find(s => String(s.id) === selected);

  // Built-in TARGETS only covers the hardcoded scenarios (1A-6B) — custom
  // scenario templates store their own targets in load_scenarios and must be
  // merged in here too, or any session tagged with a custom scenario (same
  // fix as GPSData.js's allTargets) silently shows no targets.
  const allTargets = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.targets; });
    return { ...TARGETS, ...custom };
  }, [customTemplates]);

  const enriched = useMemo(() => {
    if (!session) return [];
    const relevantPlayers = mode === 'rehab'
      ? session.players.filter(p => p.is_rehab)
      : session.players.filter(p => !p.is_rehab);
    return relevantPlayers.map(p => {
      const db = players.find(d =>
        String(d.id) === String(p.player_id) ||
        (d.name && p.player_name && d.name.toLowerCase() === p.player_name.toLowerCase())
      );
      const pos      = db?.position || p.position || null;
      const scenario = session.scenario;
      const mdLabel  = p.md_label || p.mdLabel;
      const rawTargets = scenario && mdLabel && pos && allTargets[scenario]?.[mdLabel]?.[pos]
        ? allTargets[scenario][mdLabel][pos] : null;
      const assignment = scenarios.find(sc =>
        sc.scenario === scenario &&
        (sc.playerId === 'team' || String(sc.playerId) === String(p.player_id || db?.id))
      );
      // MD (match day) is never scaled by the scenario's training intensity —
      // it's scaled by actual match minutes vs. a 90-min baseline, plus a 10%
      // warmup allowance, matching GPSData.js and the Scenario Editor.
      const intensity = mdLabel === 'MD'
        ? ((assignment?.md_minutes ?? 90) / 90) * 1.1
        : (assignment?.intensity ?? 100) / 100;
      const scaledTargets = rawTargets && intensity !== 1
        ? Object.fromEntries(Object.entries(rawTargets).map(([k, { upper, lower }]) => [
            k, { upper: Math.round(upper * intensity), lower: Math.round(lower * intensity) }
          ]))
        : rawTargets;
      // A manual day-specific override (set when planning the scenario) takes full
      // precedence over the computed/intensity-scaled targets — same lookup as
      // GPSData.js.
      const dayOverride = assignment?.target_overrides?.[session.date]?.[pos];
      const targets = dayOverride || scaledTargets;
      return {
        ...p,
        name: p.player_name || p.name || 'Unknown',
        position: pos, mdLabel, targets,
        metrics: {
          td: p.td ?? 0, rd: p.rd ?? 0, hid: p.hid ?? 0, sd: p.sd ?? 0,
          sa: p.sa ?? 0, expl: p.expl ?? 0, hmld: p.hmld ?? 0, HMLD: p.hmld ?? 0,
          max_speed: p.max_speed ?? null, minutes_played: p.minutes_played ?? null,
        },
      };
    });
  }, [session, players, scenarios, allTargets, mode]);

  const teamTotals = useMemo(() => {
    const n = enriched.length || 1;
    return {
      TD:   enriched.reduce((s, p) => s + (p.metrics.td   || 0), 0) / n,
      HID:  enriched.reduce((s, p) => s + (p.metrics.hid  || 0), 0) / n,
      SD:   enriched.reduce((s, p) => s + (p.metrics.sd   || 0), 0) / n,
      SA:   enriched.reduce((s, p) => s + (p.metrics.sa   || 0), 0) / n,
      HMLD: enriched.reduce((s, p) => s + (p.metrics.hmld || 0), 0) / n,
    };
  }, [enriched]);

  const teamTargets = useMemo(() => {
    const result = {};
    ['TD', 'HID', 'SD', 'SA', 'HMLD'].forEach(k => {
      const lowers = enriched.filter(p => p.targets?.[k]).map(p => p.targets[k].lower);
      const uppers = enriched.filter(p => p.targets?.[k]).map(p => p.targets[k].upper);
      result[k] = lowers.length
        ? { lower: lowers.reduce((a, b) => a + b, 0) / lowers.length, upper: uppers.reduce((a, b) => a + b, 0) / uppers.length }
        : null;
    });
    return result;
  }, [enriched]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div>
  );
  if (!sessions.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>No GPS sessions available.</div>
  );
  if (!filteredSessions.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
      {mode === 'rehab' ? 'No rehab sessions yet.' : 'No data for this season. Change the season filter in Settings.'}
    </div>
  );

  const myName = playerRecord?.name?.toLowerCase();

  return (
    <div style={{ padding: '0 12px 20px' }}>
      {/* Session picker */}
      <select value={selected} onChange={e => setSelected(e.target.value)}
        style={{ width: '100%', height: 48, borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--border)', color: 'var(--text-primary)', fontSize: 14, padding: '0 12px', marginBottom: 14, outline: 'none', fontFamily: 'inherit' }}>
        {[...filteredSessions].sort((a, b) => new Date(b.date) - new Date(a.date)).map(s => (
          <option key={s.id} value={s.id}>{s.date} — {s.title || 'Session'}</option>
        ))}
      </select>

      {session && (
        <>
          {/* Session info */}
          <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{session.title || 'Session'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{session.date} · {enriched.length} players</div>
            {enriched[0]?.mdLabel && (
              <span style={{ fontSize: 11, color: '#a5b4fc', background: '#312e81', padding: '2px 8px', borderRadius: 99, display: 'inline-block', marginTop: 6, fontWeight: 600 }}>
                {enriched[0].mdLabel} · Scenario {session.scenario}
              </span>
            )}
          </div>

          {/* Gauge row — horizontal scroll */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Team averages</div>
            <div style={{ display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {GAUGE_METRICS.map(m => (
                <MobileGauge key={m.key} label={m.label} value={teamTotals[m.key] || 0} targets={teamTargets[m.key]} />
              ))}
            </div>
          </div>

          {/* Player table — tap a column header to sort */}
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Players ({enriched.length}) · tap a column to sort
          </div>
          <PlayerTable players={enriched} myName={myName} />
        </>
      )}
    </div>
  );
}
